import api from './api';
import type { Rental, PaginatedResponse, RentalStatus } from '@/types';

export interface ListRentalsParams {
  /** One status, or several. The API splits this on commas, so an array is joined
   *  rather than repeated as a query parameter. Typing this as a single status made
   *  the orders list send only the first of a multi-select, silently narrowing the
   *  result to one status while the UI showed several ticked. */
  status?: RentalStatus | RentalStatus[];
  /** Planned dispatch date range, inclusive. ISO dates (YYYY-MM-DD). */
  sendDateFrom?: string;
  sendDateTo?: string;
  /** Everything due out: send date today or earlier, and not yet shipped. */
  dueForDispatch?: boolean;
  clinicId?: string;
  page?: number;
  limit?: number;
  search?: string;
  isDemo?: boolean;
  isProbono?: boolean;
  isConsignment?: boolean;
  isClinicFulfil?: boolean;
  isReplenishment?: boolean;
  hasClinic?: boolean;
}

export interface CustomShippingAddress {
  name?: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  province: string;
  postalCode: string;
  country?: string;
  phone?: string;
}

export interface CreateAdminRentalDto {
  clinicId?: string;
  patientId?: string;
  doctorId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  productSku: string;
  productName: string;
  unitPrice: number;
  isBilateral?: boolean;
  rateType?: 'retail' | 'wholesale';
  wrapSku?: string;
  wrapName?: string;
  addOns?: { sku: string; name: string; quantity: number; unitPrice: number }[];
  rentalStartDate: string;
  rentalWeeks: number;
  isDemo?: boolean;
  isProbono?: boolean;
  isConsignment?: boolean;
  /** Waives HST on this order. Applied server-side so the stored total matches the UI. */
  taxExempt?: boolean;
  shippingAddressType?: string;
  customShippingAddress?: CustomShippingAddress;
  notes?: string;
}

export const rentalsApi = {
  list: async (params: ListRentalsParams = {}) => {
    // The API splits `status` on commas. Axios would serialise an array as
    // status[]=a&status[]=b, which it does not parse, so join here rather than
    // leaving each caller to remember.
    const { status, ...rest } = params;
    const query = {
      ...rest,
      ...(status !== undefined && {
        status: Array.isArray(status) ? status.join(',') : status,
      }),
    };
    const response = await api.get<PaginatedResponse<Rental>>('/rentals', { params: query });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<Rental>(`/rentals/${id}`);
    return response.data;
  },

  getByOrderNumber: async (orderNumber: string) => {
    const response = await api.get<Rental>(`/rentals/order/${orderNumber}`);
    return response.data;
  },

  /**
   * Full refund. Routes through the state machine (refund_processed trigger,
   * valid from cancelled/complete) — the old updateStatus helper PATCHed a
   * route that never existed, so Refund always 404'd.
   */
  refund: async (id: string, notes?: string) => {
    const response = await api.post<Rental>(`/rentals/${id}/transition`, {
      trigger: 'refund_processed',
      notes,
    });
    return response.data;
  },

  createReplacement: async (id: string, reason: string) => {
    const response = await api.post<{ originalId: string; replacement: Rental }>(
      `/rentals/${id}/create-replacement`,
      { reason },
    );
    return response.data;
  },

  /**
   * Add line items to an order that has not shipped yet. The server reprices and
   * refuses once the order has left the warehouse.
   */
  addItems: async (
    id: string,
    items: Array<{
      productSku: string;
      productName: string;
      quantity: number;
      unitPrice: number;
      itemType?: string;
      variantId?: string;
    }>,
  ) => {
    const { data } = await api.post(`/rentals/${id}/items`, { items });
    return data;
  },

  addComment: async (id: string, notes: string, customerNotified = false) => {
    const response = await api.post(`/rentals/${id}/comments`, { notes, customerNotified });
    return response.data;
  },

  reportIssue: async (id: string, notes: string) => {
    const response = await api.post<Rental>(`/rentals/${id}/report-issue`, { notes });
    return response.data;
  },

  resolveIssue: async (id: string, notes?: string) => {
    const response = await api.post<Rental>(`/rentals/${id}/resolve-issue`, { notes });
    return response.data;
  },

  getShipments: async (id: string) => {
    const response = await api.get(`/shipping/rental/${id}/shipments`);
    return response.data;
  },

  /**
   * Cancel an order. Routes through the state-machine transition endpoint with
   * the `admin_cancel` trigger (valid from pending / processing / ready_to_ship).
   * The old code PATCHed a non-existent `/status` route, so cancel always 404'd.
   */
  cancel: async (id: string, notes?: string) => {
    const response = await api.post<Rental>(`/rentals/${id}/transition`, {
      trigger: 'admin_cancel',
      notes,
    });
    return response.data;
  },

  /** "Not a duplicate": releases a possible-duplicate order for dispatch. */
  clearDuplicate: async (id: string) => {
    const response = await api.post<Rental>(`/rentals/${id}/duplicate/clear`);
    return response.data;
  },

  getHistory: async (id: string) => {
    const response = await api.get(`/rentals/${id}/history`);
    return response.data;
  },

  createAdmin: async (dto: CreateAdminRentalDto) => {
    const response = await api.post<Rental>('/rentals', dto);
    return response.data;
  },

  updateAddress: async (
    id: string,
    dto: {
      shippingAddressType?: string;
      patientId?: string;
      customAddress?: CustomShippingAddress;
    },
  ) => {
    const response = await api.patch<Rental>(`/rentals/${id}/address`, dto);
    return response.data;
  },

  reassign: async (
    id: string,
    dto: { clinicId?: string | null; patientId?: string | null; reason?: string },
  ) => {
    const response = await api.patch<Rental>(`/rentals/${id}/reassign`, dto);
    return response.data;
  },

  /**
   * Correct the contact details on an order.
   *
   * `updatePatientProfile` decides whether this is a correction to the patient's
   * record or a one-off for this order — the order keeps its own copy of the details
   * used at the time, so fixing one does not fix the other.
   */
  updateContact: async (
    id: string,
    dto: {
      customerEmail?: string;
      customerPhone?: string;
      patientPhone?: string;
      updatePatientProfile?: boolean;
    },
  ) => {
    const response = await api.patch<Rental>(`/rentals/${id}/contact`, dto);
    return response.data;
  },

  /**
   * Set the end date to any date, rather than adding whole weeks via an extension.
   *
   * The original end date is preserved server-side, and overdue is measured against
   * this new date — so moving it forward takes an order off the overdue report, and
   * moving it back puts one on.
   */
  updateEndDate: async (id: string, newEndDate: string) => {
    const response = await api.patch<Rental>(`/rentals/${id}/dates`, { newEndDate });
    return response.data;
  },
};

export default rentalsApi;
